// src/components/admin/PaystackConfigPanel.tsx
// DROP 18a — Platform Admin can update Paystack credentials

import React, { useState, useEffect } from "react";
import { Key, Eye, EyeOff, CheckCircle, AlertTriangle, RefreshCw, ExternalLink } from "lucide-react";

interface PaystackConfig {
  publicKey: string;
  secretKeyMasked: string;
  mode: "test" | "live";
  webhookUrl: string;
  lastUpdated?: string;
}

export default function PaystackConfigPanel() {
  const [config,   setConfig]   = useState<PaystackConfig | null>(null);
  const [editMode, setEditMode] = useState(false);
  const [pubKey,   setPubKey]   = useState("");
  const [secKey,   setSecKey]   = useState("");
  const [showSec,  setShowSec]  = useState(false);
  const [mode,     setMode]     = useState<"test"|"live">("test");
  const [busy,     setBusy]     = useState(false);
  const [msg,      setMsg]      = useState<{type:"ok"|"err"; text:string}|null>(null);

  const token = () => localStorage.getItem("erue_auth_token") || "";

  const flash = (type: "ok"|"err", text: string) => {
    setMsg({type, text}); setTimeout(() => setMsg(null), 4000);
  };

  const load = async () => {
    try {
      const res = await fetch("/api/admin/paystack-config", {
        headers: { Authorization: `Bearer ${token()}` }
      });
      const d = await res.json();
      if (d.success) {
        setConfig(d.config);
        setPubKey(d.config.publicKey || "");
        setMode(d.config.mode || "test");
      }
    } catch { flash("err", "Failed to load Paystack config"); }
  };

  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!pubKey.trim() || !secKey.trim()) {
      flash("err", "Both Public Key and Secret Key are required"); return;
    }
    if (!pubKey.startsWith("pk_")) {
      flash("err", "Public key must start with pk_test_ or pk_live_"); return;
    }
    if (!secKey.startsWith("sk_")) {
      flash("err", "Secret key must start with sk_test_ or sk_live_"); return;
    }
    setBusy(true);
    try {
      const res = await fetch("/api/admin/paystack-config", {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token()}` },
        body: JSON.stringify({ publicKey: pubKey, secretKey: secKey, mode })
      });
      const d = await res.json();
      if (d.success) {
        flash("ok", "Paystack credentials updated successfully");
        setEditMode(false); setSecKey(""); await load();
      } else { flash("err", d.error || "Failed to save"); }
    } catch { flash("err", "Connection error"); }
    finally { setBusy(false); }
  };

  const testConnection = async () => {
    setBusy(true);
    try {
      const res = await fetch("/api/admin/paystack-test", {
        headers: { Authorization: `Bearer ${token()}` }
      });
      const d = await res.json();
      if (d.success) flash("ok", `✓ Connected to Paystack (${d.mode} mode)`);
      else flash("err", d.error || "Paystack connection failed");
    } catch { flash("err", "Connection test failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <Key size={16} style={{ color: "#C9A84C" }}/>
          <span className="font-semibold text-sm" style={{ color: "#EDE8DC" }}>
            Paystack Payment Gateway
          </span>
        </div>
        <a href="https://dashboard.paystack.com" target="_blank" rel="noopener noreferrer"
           className="flex items-center gap-1 text-xs transition-colors"
           style={{ color: "rgba(201,168,76,0.7)" }}>
          Dashboard <ExternalLink size={11}/>
        </a>
      </div>

      {/* Flash */}
      {msg && (
        <div className="px-4 py-3 rounded-xl text-sm flex items-center gap-2"
             style={{
               background: msg.type === "ok" ? "rgba(74,222,128,0.1)" : "rgba(239,68,68,0.1)",
               border: `1px solid ${msg.type === "ok" ? "rgba(74,222,128,0.3)" : "rgba(239,68,68,0.3)"}`,
               color: msg.type === "ok" ? "#4ADE80" : "#FCA5A5"
             }}>
          {msg.type === "ok" ? <CheckCircle size={14}/> : <AlertTriangle size={14}/>}
          {msg.text}
        </div>
      )}

      {/* Current config display */}
      {config && !editMode && (
        <div className="rounded-xl p-5 space-y-3" style={{ background: "#121E16", border: "1px solid #1C2E22" }}>
          <div className="flex items-center justify-between">
            <span className="text-xs" style={{ color: "rgba(237,232,220,0.5)" }}>Mode</span>
            <span className={`text-xs px-2 py-0.5 rounded-full font-medium border ${
              config.mode === "live"
                ? "text-emerald-400 bg-emerald-400/10 border-emerald-400/30"
                : "text-yellow-400 bg-yellow-400/10 border-yellow-400/30"
            }`}>
              {config.mode.toUpperCase()}
            </span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-xs" style={{ color: "rgba(237,232,220,0.5)" }}>Public Key</span>
            <span className="text-xs font-mono" style={{ color: "#EDE8DC" }}>
              {config.publicKey ? `${config.publicKey.slice(0, 16)}…` : "Not set"}
            </span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-xs" style={{ color: "rgba(237,232,220,0.5)" }}>Secret Key</span>
            <span className="text-xs font-mono" style={{ color: "#EDE8DC" }}>
              {config.secretKeyMasked || "Not set"}
            </span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-xs" style={{ color: "rgba(237,232,220,0.5)" }}>Webhook URL</span>
            <span className="text-xs font-mono" style={{ color: "rgba(237,232,220,0.4)" }}>
              {config.webhookUrl}
            </span>
          </div>
          {config.lastUpdated && (
            <div className="flex items-center justify-between">
              <span className="text-xs" style={{ color: "rgba(237,232,220,0.5)" }}>Last Updated</span>
              <span className="text-xs" style={{ color: "rgba(237,232,220,0.4)" }}>
                {new Date(config.lastUpdated).toLocaleDateString()}
              </span>
            </div>
          )}
          <div className="flex gap-2 mt-4">
            <button onClick={() => setEditMode(true)}
                    className="flex-1 py-2 rounded-lg text-xs font-medium transition-all"
                    style={{ background: "#C9A84C", color: "#070D0A" }}>
              Update Credentials
            </button>
            <button onClick={testConnection} disabled={busy}
                    className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs border transition-all"
                    style={{ borderColor: "#1C2E22", color: "rgba(237,232,220,0.6)" }}>
              <RefreshCw size={11} className={busy ? "animate-spin" : ""}/>
              Test
            </button>
          </div>
        </div>
      )}

      {/* Edit form */}
      {editMode && (
        <div className="rounded-xl p-5 space-y-4" style={{ background: "#121E16", border: "1px solid rgba(201,168,76,0.2)" }}>
          {/* Mode toggle */}
          <div>
            <label className="text-xs uppercase tracking-wider mb-2 block"
                   style={{ color: "rgba(237,232,220,0.5)" }}>Mode</label>
            <div className="flex gap-2">
              {(["test","live"] as const).map(m => (
                <button key={m} onClick={() => setMode(m)}
                        className="flex-1 py-2 rounded-lg text-xs font-medium transition-all border"
                        style={{
                          background: mode === m ? (m === "live" ? "rgba(74,222,128,0.15)" : "rgba(250,204,21,0.15)") : "transparent",
                          borderColor: mode === m ? (m === "live" ? "rgba(74,222,128,0.4)" : "rgba(250,204,21,0.4)") : "#1C2E22",
                          color: mode === m ? (m === "live" ? "#4ADE80" : "#FDE047") : "rgba(237,232,220,0.4)"
                        }}>
                  {m.toUpperCase()}
                </button>
              ))}
            </div>
          </div>

          {/* Public key */}
          <div>
            <label className="text-xs uppercase tracking-wider mb-1.5 block"
                   style={{ color: "rgba(237,232,220,0.5)" }}>
              Public Key <span style={{ color: "#C9A84C" }}>*</span>
            </label>
            <input value={pubKey} onChange={e => setPubKey(e.target.value)}
                   placeholder={`pk_${mode}_...`}
                   className="w-full px-3 py-2.5 rounded-lg text-sm font-mono outline-none"
                   style={{ background: "#0E1812", border: "1px solid #1C2E22", color: "#EDE8DC" }}/>
          </div>

          {/* Secret key */}
          <div>
            <label className="text-xs uppercase tracking-wider mb-1.5 block"
                   style={{ color: "rgba(237,232,220,0.5)" }}>
              Secret Key <span style={{ color: "#C9A84C" }}>*</span>
            </label>
            <div className="relative">
              <input type={showSec ? "text" : "password"} value={secKey}
                     onChange={e => setSecKey(e.target.value)}
                     placeholder={`sk_${mode}_...`}
                     className="w-full px-3 py-2.5 pr-10 rounded-lg text-sm font-mono outline-none"
                     style={{ background: "#0E1812", border: "1px solid #1C2E22", color: "#EDE8DC" }}/>
              <button onClick={() => setShowSec(!showSec)} type="button"
                      className="absolute right-3 top-1/2 -translate-y-1/2"
                      style={{ color: "rgba(237,232,220,0.3)" }}>
                {showSec ? <EyeOff size={14}/> : <Eye size={14}/>}
              </button>
            </div>
            <p className="text-xs mt-1" style={{ color: "rgba(237,232,220,0.3)" }}>
              Secret key is encrypted at rest and never exposed in API responses.
            </p>
          </div>

          <div className="flex gap-2 mt-2">
            <button onClick={() => { setEditMode(false); setSecKey(""); }}
                    className="flex-1 py-2 rounded-lg text-xs border transition-all"
                    style={{ borderColor: "#1C2E22", color: "rgba(237,232,220,0.5)" }}>
              Cancel
            </button>
            <button onClick={save} disabled={busy}
                    className="flex-1 py-2 rounded-lg text-xs font-semibold transition-all"
                    style={{ background: "#C9A84C", color: "#070D0A" }}>
              {busy ? "Saving…" : "Save Credentials"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
